import { Button, Section, SubmitButton } from "@dgs/core";
import { Form, Formik } from "formik";
import { FC, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import styled from "styled-components";
import { HotelStayStatus } from "../../../admin/src/shared/types/hotelStay";
import { FormikScrollToError } from "../../../shared/FormikScrollToError";
import { usePMAnalytics } from "../analytics/PMAnalyticsProvider";
import {
	RegistrationFormStepChildrenResource,
	RegistrationFormStepResource,
	TravelSectionSettingsResource,
} from "../shared/types";
import { HotelSectionFormValues, HotelStayFormValues } from "../shared/types/hotel";
import { BlockRenderer } from "./BlockRenderer";
import { useRegistrationForm } from "./provider/registrationFormProviderContext";
import { createInitialValues, getSectionsFromStep, toGuestAttributeChildrenFromSection } from "./registrationFormUtils";
import { ResetStepFormValuesForHiddenDataFields } from "./ResetStepFormValuesForHiddenDataFields";
import { RegistrationSection } from "./section/RegistrationSection";

const StyledSection = styled(Section)`
	padding: ${({ theme }) => theme.spacing(4)};
`;

const ButtonWrapper = styled.div`
	display: flex;
	justify-content: flex-end;
	padding: 0 0 ${({ theme }) => theme.spacing(4)} 0;
	gap: ${({ theme }) => theme.spacing(4)};
`;

interface Props {
	step: RegistrationFormStepResource;
	travelSectionSettings: TravelSectionSettingsResource | null;
}

const getButtonSectionColor = (children: RegistrationFormStepChildrenResource[]) => {
	const lastSection = children.length > 0 ? children[children.length - 1] : null;

	if (lastSection?.entityType === "section") {
		return lastSection.section.backgroundColor;
	}
};

export const RegistrationStep: FC<Props> = ({ step, travelSectionSettings }) => {
	const { registrationId = "", stepId = "" } = useParams<{ stepId: string; registrationId: string }>();
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { trackNextStep, trackPreviousStep, trackRegistrationAborted, trackSummaryStarted } = usePMAnalytics();
	const {
		filledSteps,
		registrationForm: { guest, landingBlockId },
		validateStep,
		previousStep,
		submitStep,
		landingConsents,
	} = useRegistrationForm();

	const previousStepId = useMemo(() => previousStep(step.id), [previousStep, step.id]);

	const onPreviousStepClick = useCallback(() => {
		if (previousStepId === null) {
			if (!!landingConsents.length || !!landingBlockId) {
				trackRegistrationAborted(registrationId, step.id);
				navigate("../landing-page");
			}
		} else {
			trackPreviousStep(registrationId, step.id, Number(previousStepId));
			navigate(`../${previousStepId}`);
		}
	}, [
		landingBlockId,
		landingConsents.length,
		navigate,
		previousStepId,
		registrationId,
		step.id,
		trackPreviousStep,
		trackRegistrationAborted,
	]);

	const initialValues = useMemo(() => {
		return (
			filledSteps[step.id] ?? {
				...createInitialValues(
					getSectionsFromStep(step)
						.filter(({ section }) => section.type === "default")
						.flatMap(({ section }) => toGuestAttributeChildrenFromSection(section)),
					guest?.guestAttributeValues,
				),
				hotelSections: (guest?.hotelStays || []).reduce(
					(hotelSectionsGroupedByStayType, hotelStay): Record<number, HotelSectionFormValues> => {
						const formValues: HotelSectionFormValues = hotelSectionsGroupedByStayType[hotelStay.hotelStayTypeId] || {
							hotelStays: [],
						};
						const hotelStayFormValues: HotelStayFormValues = {
							...hotelStay,
							cancellationRequested: hotelStay.status === HotelStayStatus.CANCELLATION_REQUESTED,
						};

						return {
							...hotelSectionsGroupedByStayType,
							[hotelStay.hotelStayTypeId]: {
								...formValues,
								hotelStays: [...formValues.hotelStays, hotelStayFormValues],
							},
						};
					},
					{} as Record<number, HotelSectionFormValues>,
				),
			}
		);
	}, [filledSteps, step, guest?.guestAttributeValues, guest?.hotelStays]);

	const onSubmit = useCallback(
		async (values: Record<string, any>) => {
			const nextStepId = await submitStep(step.id, values);

			if (!nextStepId) {
				trackSummaryStarted(registrationId, Number(stepId));
				return navigate("../summary");
			}
			trackNextStep(registrationId, Number(stepId), nextStepId);
			return navigate(`../${nextStepId}`);
		},
		[navigate, registrationId, step.id, stepId, submitStep, trackNextStep, trackSummaryStarted],
	);

	const validate = useCallback(
		(values: Record<string, any>) => {
			return validateStep(step, values);
		},
		[step, validateStep],
	);

	const buttonSectionColor = useMemo(() => getButtonSectionColor(step.children) || "content", [step.children]);

	return (
		<Formik key={step.id} initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
			<Form>
				<ResetStepFormValuesForHiddenDataFields step={step} />
				{step.children.map((sectionOrBlock) => {
					if (sectionOrBlock.entityType === "block") {
						return (
							<BlockRenderer
								key={`${sectionOrBlock.entityType}_${sectionOrBlock.block.id}`}
								block={sectionOrBlock.block}
							/>
						);
					}
					return (
						<RegistrationSection
							key={`${sectionOrBlock.entityType}_${sectionOrBlock.section.id}`}
							registrationFormId={registrationId}
							guestId={guest.id === "new" ? null : guest.id}
							section={sectionOrBlock.section}
							validationRules={sectionOrBlock.section.validationRules}
							stepId={step.id}
							travelSectionSettings={travelSectionSettings}
						/>
					);
				})}
				<StyledSection color={buttonSectionColor}>
					<ButtonWrapper>
						{(previousStepId || !!landingConsents.length || !!landingBlockId) && (
							<Button type="button" onClick={onPreviousStepClick}>
								{t("Back")}
							</Button>
						)}
						<SubmitButton color="primary">{t("Next")}</SubmitButton>
					</ButtonWrapper>
				</StyledSection>
				<FormikScrollToError />
			</Form>
		</Formik>
	);
};
